import { Children, ReactNode, ElementType, FormEvent, FC, cloneElement } from 'react';
import { FormControlLabel } from '@material-ui/core';

interface FieldPropProvider {
    (formControl: FormControl, elmProps: ElmProps): object;
}

type ElmProps = Record<string, any> & {
    type?: string;
    name: string;
    errorName?: string;
    elmType: ElementType;
}

export interface OnChange {
    (name: string, value: any): void;
}

type FormControl = {
    values: Values;
    errors: Errors;
    onChange: OnChange;
}

type Errors = {
    [key: string]: string;
}

export type Values = {
    [key: string]: any;
}

interface Props {
    children?: ReactNode;
    fieldPropProvider: FieldPropProvider;
    onChange: OnChange;
    data: Values;
    errors: Errors;
    onSubmit: (value: FormEvent<HTMLFormElement>) => void;
    [x:string]: any;
}

export const ControlledForm: FC<Props> = (props) => {
    const { children, fieldPropProvider, onChange, data, errors, onSubmit, ...other } = props;

    const handleSubmit = (customOnClickHandler?: () => any) => (event: FormEvent<HTMLFormElement>) => {
        customOnClickHandler && customOnClickHandler();
        return props.onSubmit(event);
    }

    const renderChild = (elm: any) => {
        if (!(elm && elm.type)) return elm; // We are not a valid input element
        let { type: Type, props: { name, errorName, inputRef, uncontrolled, children, ...elmProps }} = elm;
        if (elmProps.type === 'submit') return cloneElement(elm, {onClick: handleSubmit(elm.props.onClick)}); // We are a wrapped child

        const formControl = {
            values: props.data,
            errors: props.errors,
            onChange: props.onChange
        };

        const fieldProps = propsForField(formControl, props.fieldPropProvider, { ...elm.props, propTypes: Type.propTypes, elmType: elm.type });
        children = Children.map(children, child => typeof child !== 'string' ? renderChild(child) : child);

        return <Type ref={inputRef} {...{...fieldProps, ...elmProps}}>{children && children.length === 1 ? children[0] : children}</Type>
    }

    return <form onSubmit={handleSubmit()} {...other}>{Children.map(props.children, renderChild)}</form>
}

const propsForField = (formControl: FormControl, fieldPropProvider: FieldPropProvider, props: ElmProps) => {
    let fieldProps = {};
    // We've opted in to the being controlled by the form. Let's create some default props
    if (props.name !== undefined && !props.uncontrolled) fieldProps = fieldPropProvider(formControl, props);
    return fieldProps;
}

const onChange = (elmProps: ElmProps, onChange: OnChange) => (event: React.ChangeEvent<any>) => {
    const { type, elmType, name } = elmProps;
    const { target: { value, checked }} = event;
    if (type === 'checkbox' || elmType == FormControlLabel) return onChange(name, checked);
    if (type === 'number') return onChange(name, parseFloat(value) || '');
    return onChange(name, value);
}

const propsForMuiField: FieldPropProvider = (formControl, elmProps) => {
    const {type, name, errorName, elmType} = elmProps;
    const props: any = {};

    if (type === 'checkbox' || elmType == FormControlLabel) {
        props.checked = deepGet(name, formControl.values) || false;
    } else {
        props.value = formControl.values && deepGet(name, formControl.values);
        if (props.value === undefined || props.value === null) props.value = '';
    }

    props.onChange = onChange(elmProps, formControl.onChange);
    const errorKey = errorName || name;

    if (formControl.errors[errorKey] && elmProps.touched)  {
        const errorText = formControl.errors[errorKey];
        props.error = true;
        props.helperText = errorText;
    }

    props.name = name;
    props.placeholder = '';
    return props;
}

function deepGet(name: string, object: any) {
    return name.split(/(?:\[(?=\d)|(?!=\d)\]\.?|\.)/).filter(x => x !== '').reduce((memo, part) => memo ? memo[part] : null, object);
}

export default (props: any) => <ControlledForm fieldPropProvider={propsForMuiField} {...props} />
